import React from 'react';
import {
  useQuery,
  QueryClientProvider,
  QueryClient,
  useInfiniteQuery,
} from 'react-query';
import * as R from 'ramda';
import {useInView} from 'react-intersection-observer';
import clsx from 'clsx';
import {useRouteParams, Link, useUrl} from '@shopify/hydrogen';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/dist/ScrollTrigger';
//components
import Product from '../custom/BFSProduct.client';
import CollectionFilter from './CollectionFilter.client';
import MobileFilterSort from './CollectionListingMobileFilterSort.client';
import Sorting from './CollectionSorting.client';

//types
import type {
  ProductWithNodes,
  BFSProductVariant,
  BFSProductType,
  FilterState,
  FilterAction,
  SanityProductSettings,
  SantiyCollectionLevel as SanityCollectionLevel,
  SanityShopifyCollection,
} from '../../types';
import type {FilterOption} from './CollectionFilter.client';

//TODO: collection translations

//context
import {TranslationsContext} from '../../TranslationProvider.client';

gsap.registerPlugin(ScrollTrigger);

export function LoadingSpinner({
  className,
  width,
}: {
  className?: string;
  width?: string;
}) {
  const id = React.useId();
  return (
    <svg
      viewBox="0 0 38 38"
      xmlns="http://www.w3.org/2000/svg"
      className={`fill-current ${width ? width : 'w-full'} ${className}`}
    >
      <defs>
        <linearGradient x1="8.042%" y1="0%" x2="65.682%" y2="23.865%" id={id}>
          <stop stopColor="currentColor" stopOpacity="0" offset="0%" />
          <stop stopColor="currentColor" stopOpacity=".631" offset="63.146%" />
          <stop stopColor="currentColor" offset="100%" />
        </linearGradient>
      </defs>
      <g fill="none" fillRule="evenodd">
        <g transform="translate(1 1)">
          <path
            d="M36 18c0-9.94-8.06-18-18-18"
            id="Oval-2"
            stroke={`url(#${id})`}
            strokeWidth="2"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 18 18"
              to="360 18 18"
              dur="0.9s"
              repeatCount="indefinite"
            />
          </path>
          <circle fill="currentColor" cx="36" cy="18" r="1">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 18 18"
              to="360 18 18"
              dur="0.9s"
              repeatCount="indefinite"
            />
          </circle>
        </g>
      </g>
    </svg>
  );
}

interface Props {
  collectionId: string;
  vendor?: string | undefined | null;
  collectionTitle: string;
  collectionStructure:
    | SanityProductSettings['collection_structure']
    | null
    | undefined;
}

const queryClient = new QueryClient();

export default function CollectionListingWrapper({
  collectionId,
  vendor,
  collectionTitle,
  collectionStructure,
}: Props) {
  return (
    <QueryClientProvider client={queryClient}>
      <CollectionListingMain
        collectionId={collectionId}
        vendor={vendor}
        collectionTitle={collectionTitle}
        collectionStructure={collectionStructure}
      />
    </QueryClientProvider>
  );
}

function CollectionListingMain({
  collectionId,
  vendor,
  collectionTitle,
  collectionStructure,
}: Props) {
  const {ref, inView} = useInView();
  const {handle} = useRouteParams();
  const translations = React.useContext(TranslationsContext);

  //build collection structure
  const findNestedLevel = (
    data: SanityCollectionLevel[],
    gid: string,
    level: number = 1,
  ): number => {
    if (!data || !Array.isArray(data)) {
      return -1;
    }

    return data.reduce((acc: number, item: SanityCollectionLevel): number => {
      if (item.this_collection && item.this_collection.store.gid === gid) {
        return level;
      }

      if (item.associated_collections) {
        const nestedLevel = findNestedLevel(
          item.associated_collections.flatMap((ac) => ac.collections),
          gid,
          level + 1,
        );
        return nestedLevel !== -1 ? nestedLevel : acc;
      }

      return acc;
    }, -1);
  };

  const gidExists = (
    data: SanityCollectionLevel[],
    gidToFind: string,
  ): boolean => {
    if (!data || !Array.isArray(data)) {
      return false;
    }

    return data.some((item) =>
      item.this_collection.store.gid === gidToFind
        ? true
        : item.associated_collections
        ? item.associated_collections.some((ac) =>
            gidExists(ac.collections, gidToFind),
          )
        : false,
    );
  };

  const isCollectionInHiererachy =
    collectionStructure &&
    gidExists(collectionStructure.collections, collectionId);

  const collectionLevel = isCollectionInHiererachy
    ? findNestedLevel(collectionStructure.collections, collectionId)
    : -1;

  // findImmediateChildren function
  const findImmediateChildren = (
    data: SanityCollectionLevel[],
    collectionId: string,
  ): SanityCollectionLevel[] | null => {
    if (!data || !Array.isArray(data)) {
      return null;
    }

    const reducer = (
      result: SanityCollectionLevel[] | null,
      item: SanityCollectionLevel,
    ): SanityCollectionLevel[] | null => {
      if (result) {
        return result;
      }

      if (item.this_collection.store.gid === collectionId) {
        return item.associated_collections
          ? item.associated_collections.flatMap((ac) => ac.collections)
          : [];
      }

      const nestedResult = item.associated_collections
        ? item.associated_collections.reduce(
            (acc: SanityCollectionLevel[] | null, ac) =>
              findImmediateChildren(ac.collections, collectionId) || acc,
            null as SanityCollectionLevel[] | null,
          )
        : null;

      return nestedResult;
    };

    return data.reduce(reducer, null as SanityCollectionLevel[] | null);
  };

  const findSiblings = (
    data: SanityCollectionLevel[],
    targetId: string,
  ): SanityCollectionLevel[] | null => {
    if (!data || !Array.isArray(data)) {
      return null;
    }

    for (const item of data) {
      if (item.this_collection.store.gid === targetId) {
        return data.filter(
          (sibling) => sibling.this_collection.store.id !== targetId,
        );
      }

      const siblings =
        item?.associated_collections &&
        findSiblings(
          item.associated_collections.map((ac) => ac.collections).flat(),
          targetId,
        );
      if (siblings) {
        return siblings;
      }
    }

    return null;
  };

  const findParents = (
    data: SanityCollectionLevel[],
    targetGid: string,
  ): SanityShopifyCollection[] => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    const foundParents: SanityShopifyCollection[] = data.flatMap((item) => {
      const collections = item.associated_collections
        ? item.associated_collections.flatMap((ac) => ac.collections)
        : [];

      if (
        collections.some(
          (collection) => collection.this_collection.store.gid === targetGid,
        )
      ) {
        return [item.this_collection, ...findParents(collections, targetGid)];
      }

      const foundParentsInNestedCollections = findParents(
        collections,
        targetGid,
      );
      if (foundParentsInNestedCollections.length > 0) {
        return [item.this_collection, ...foundParentsInNestedCollections];
      }

      return [];
    });

    return foundParents;
  };

  const allParentCollections =
    collectionLevel > 1 && collectionStructure
      ? findParents(collectionStructure.collections, collectionId)
      : null;

  const relatedCollections =
    collectionLevel < 3 && collectionStructure
      ? findImmediateChildren(collectionStructure.collections, collectionId)
      : collectionLevel === 3 && collectionStructure
      ? findSiblings(collectionStructure.collections, collectionId)
      : null;

  const trimmedCollectionId = collectionId.replace(
    'gid://shopify/Collection/',
    '',
  );

  function filterReducer(
    state: FilterState,
    action: FilterAction,
  ): FilterState {
    switch (action.type) {
      case 'FILTER_VENDOR':
        return {...state, vendor: action.payload};
      case 'CLEAR_VENDOR':
        return {...state, vendor: ''};
      case 'FILTER_COLOR':
        if (state['pf_t_color[]'].includes(action.payload)) {
          return {
            ...state,
            'pf_t_color[]': state['pf_t_color[]'].filter(
              (b) => b !== action.payload,
            ),
          };
        }
        return {
          ...state,
          'pf_t_color[]': state['pf_t_color[]'].concat(action.payload),
        };
      case 'CLEAR_COLOR':
        return {
          ...state,
          'pf_t_color[]': state['pf_t_color[]'].filter(
            (b) => b !== action.payload,
          ),
        };
      case 'FILTER_STYLE':
        if (state['pf_t_style[]'].includes(action.payload)) {
          return {
            ...state,
            'pf_t_style[]': state['pf_t_style[]'].filter(
              (b) => b !== action.payload,
            ),
          };
        }
        return {
          ...state,
          'pf_t_style[]': state['pf_t_style[]'].concat(action.payload),
        };
      case 'CLEAR_STYLE':
        return {
          ...state,
          'pf_t_style[]': state['pf_t_style[]'].filter(
            (b) => b !== action.payload,
          ),
        };
      case 'FILTER_SIZE':
        if (state['pf_opt_size[]'].includes(action.payload)) {
          return {
            ...state,
            'pf_opt_size[]': state['pf_opt_size[]'].filter(
              (b) => b !== action.payload,
            ),
          };
        }
        return {
          ...state,
          'pf_opt_size[]': state['pf_opt_size[]'].concat(action.payload),
        };
      case 'CLEAR_SIZE':
        return {
          ...state,
          'pf_opt_size[]': state['pf_opt_size[]'].filter(
            (b) => b !== action.payload,
          ),
        };
      // case 'FILTER_AVAILABILITY':
      //   return {
      //     ...state,
      //     product_available:
      //       state.product_available === 'true' ? 'false' : 'true',
      //   };
      case 'FILTER_BRANDS':
        if (state['pf_v_brand[]'].includes(action.payload)) {
          return {
            ...state,
            'pf_v_brand[]': state['pf_v_brand[]'].filter(
              (b) => b !== action.payload,
            ),
          };
        }
        return {
          ...state,
          'pf_v_brand[]': state['pf_v_brand[]'].concat(action.payload),
        };
      case 'CLEAR_BRAND':
        return {
          ...state,
          'pf_v_brand[]': state['pf_v_brand[]'].filter(
            (b) => b !== action.payload,
          ),
        };
      case 'FILTER_TAGS':
        if (state['pf_t_tag[]'].includes(action.payload)) {
          return {
            ...state,
            'pf_t_tag[]': state['pf_t_tag[]'].filter(
              (b) => b !== action.payload,
            ),
          };
        }
        return {
          ...state,
          'pf_t_tag[]': state['pf_t_tag[]'].concat(action.payload),
        };
      case 'CLEAR_TAG':
        return {
          ...state,
          'pf_t_tag[]': state['pf_t_tag[]'].filter((b) => b !== action.payload),
        };
      case 'FILTER_PRICE_RANGE':
        return {...state, 'pf_p_price[]': action.payload};
      case 'SET_SORTING':
        return {...state, sort: action.payload};
      case 'FILTER_STOCK_STATUS':
        if (state['pf_st_stock_status[]'].includes(action.payload)) {
          return {
            ...state,
            'pf_st_stock_status[]': state['pf_st_stock_status[]'].filter(
              (b) => b !== action.payload,
            ),
          };
        }
        return {
          ...state,
          'pf_st_stock_status[]': state['pf_st_stock_status[]'].concat(
            action.payload,
          ),
        };
      case 'CLEAR_STOCK_STATUS':
        return {
          ...state,
          'pf_st_stock_status[]': state['pf_st_stock_status[]'].filter(
            (b) => b !== action.payload,
          ),
        };
      default:
        return state;
    }
  }

  const [state, dispatch] = React.useReducer(filterReducer, {
    vendor: vendor ?? '',
    'pf_t_color[]': [],
    'pf_t_style[]': [],
    'pf_opt_size[]': [],
    'pf_p_price[]': [0, 10000000],
    'pf_v_brand[]': [],
    'pf_st_stock_status[]': [],
    'pf_t_tag[]': [],
    // product_available: 'true',
    sort: 'manual',
  });

  const paramsToFilter = R.pipe(R.reject(R.isNil), R.filter(Boolean))(state);
  const queryString = `&${R.pipe(
    R.reject(R.isNil),
    R.filter(Boolean),
  )(
    Object.keys(paramsToFilter).map((key) => {
      if (key === 'pf_st_stock_status[]') {
        if (R.isEmpty(paramsToFilter[key])) {
          return null;
        }
        return paramsToFilter[key]
          .map((t: string) => {
            if (t === 'in-stock') {
              return 'true';
            } else {
              return 'false';
            }
          })
          .map((t: string) => `${key}=${t}`)
          .toString()
          .replace(/,/g, '&');
      }
      if (
        key === 'pf_v_brand[]' ||
        key === 'pf_opt_size[]' ||
        key === 'pf_t_style[]' ||
        key === 'pf_t_tag[]' ||
        key === 'pf_t_color[]'
      ) {
        if (R.isEmpty(paramsToFilter[key])) {
          return null;
        }
        return paramsToFilter[key]
          .map((t: string) => `${key}=${t}`)
          .toString()
          .replace(/,/g, '&');
      }
      if (key === 'pf_p_price[]') {
        const minPrice = new Intl.NumberFormat('zh-HK', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
          .format(parseInt(paramsToFilter[key][0]))
          .replace(/[,]/g, '');
        const maxPrice = new Intl.NumberFormat('zh-HK', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
          .format(parseInt(paramsToFilter[key][1]))
          .replace(/[,]/g, '');
        // return key + '=' + paramsToFilter[key].toString().replace(',', ':');
        return key + '=' + `${minPrice}:${maxPrice}`;
      }
      return key + '=' + paramsToFilter[key];
    }),
  ).join('&')}`;

  const {
    isLoading: bfsIsLoading,
    error: bfsError,
    data: bfsData,
    refetch: bfsRefetch,
  } = useQuery({
    queryKey: ['bfsProducts', queryString],
    queryFn: () =>
      fetch(
        `https://services.mybcapps.com/bc-sf-filter/filter?shop=helmetking-0001.myshopify.com&collection_scope=${trimmedCollectionId}&build_filter_tree=true${queryString}`,
      ).then((res) => res.json()),
  });

  const {
    data: infiniteData,
    error: infiniteDataError,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ['infiniteproducts'],
    async ({pageParam = 1}) => {
      //fetch from api
      const apiRes = await fetch(
        `https://services.mybcapps.com/bc-sf-filter/filter?shop=helmetking-0001.myshopify.com&collection_scope=${trimmedCollectionId}&build_filter_tree=true${queryString}&page=${pageParam}`,
      );
      const apiData = await apiRes.json();
      const allProducts = apiData.products.map((p: BFSProductType) => ({
        ...p,
        availableForSale: p.available,
        id: `gid://shopify/Product/${p?.id}`,
        images: {
          nodes: p?.images_info?.map((image) => ({
            ...image,
            altText: image.alt,
          })),
        },
        variants: {
          nodes: p.variants.map((variant: BFSProductVariant) => {
            return {
              ...variant,
              availableForSale: variant.available,
              compareAtPriceV2: variant.compare_at_price ?? null,
              id: `gid://shopify/ProductVariant/${variant?.id}`,
              image: {
                altText: null,
                // height: 1000,
                // width: 1000,
                url: variant.image,
              },
              selectedOptions: variant.merged_options.map((option) => {
                const optionName = option.split(':')[0];
                const optionNameCap =
                  optionName[0].toUpperCase() + optionName.substring(1);
                return {
                  name: optionNameCap,
                  value: option.split(':')[1],
                };
              }),
              priceV2: variant.price ?? null,
              compareAtPrice: variant.compare_at_price ?? null,
              price: variant.price ?? null,
            };
          }),
        },
        options: p?.options_with_values?.map((v) => {
          return {
            name: v.label,
            values: v?.values?.map((value) => value.title),
          };
        }),
      }));

      const totalProducts = apiData.total_product;
      const maxRounds = Math.ceil(totalProducts / 20);
      return {
        data: allProducts,
        nextPage: pageParam > maxRounds ? undefined : pageParam + 1,
      };
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
    },
  );

  // when loading icon is in view
  // when has next page
  // refetch
  React.useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView]);

  //refetch when query params or url params or handle change
  React.useEffect(() => {
    console.log('refetch');
    refetch();
    bfsRefetch();
  }, [handle]);

  // scroll trigger
  const filterColRef = React.useRef(null);
  const sortingRef = React.useRef(null);
  const mainRef = React.useRef(null);
  React.useEffect(() => {
    ScrollTrigger.matchMedia({
      '(min-width: 1024px)': function () {
        if (filterColRef?.current && mainRef?.current) {
          gsap.timeline({
            scrollTrigger: {
              start: 'top 217px',
              trigger: filterColRef?.current,
              endTrigger: '#email-signup',
              end: 'top bottom',
              pin: true,
              pinSpacing: false,
            },
          });
        }
        if (sortingRef?.current && mainRef?.current) {
          gsap.timeline({
            scrollTrigger: {
              start: 'top 181px',
              trigger: sortingRef?.current,
              endTrigger: '#email-signup',
              end: 'top bottom',
              pin: true,
              pinSpacing: false,
            },
          });
        }
      },
    });
  }, []);

  //Refresh scroll trigger position on loading more products
  React.useEffect(() => {
    ScrollTrigger.refresh();
  }, [infiniteData]);

  //if is vendor page, hide default breadcumb
  const {pathname} = useUrl();

  if (bfsError || infiniteDataError) return <>An error has occurred</>;

  return (
    <div className="grid lg:grid-cols-8 lg:gap-10" ref={mainRef}>
      <div className="relative lg:col-span-2">
        <div className="" ref={filterColRef}>
          <div className="lg:h-screen lg:overflow-y-scroll lg:pr-4 lg:pb-64">
            {pathname !== '/collections/vendor' && (
              <div className="fixed left-1/2 z-10 w-screen -translate-x-1/2 bg-white lg:relative lg:left-0 lg:w-full lg:translate-x-0">
                <div className="px-5 lg:px-0">
                  <nav className="text-sm md:text-mdflex flex-wrap items-center lg:mb-[34px]">
                    <Link
                      to="/"
                      className="font-light transition-colors duration-200 ease-out hover:text-hk-orange"
                    >
                      Home
                    </Link>
                    {allParentCollections &&
                      allParentCollections.map((c) => (
                        <>
                          <span className="mx-2 inline-block font-normal">
                            /
                          </span>
                          <Link
                            to={`/collections/${c.store.slug?.current}`}
                            className="font-normal transition-colors duration-200 ease-out hover:text-hk-orange"
                          >
                            {c.store.title}
                          </Link>
                        </>
                      ))}
                    <span className="mx-2 inline-block font-light">/</span>
                    <span className="font-light">{collectionTitle}</span>
                  </nav>
                </div>
                <div className="lg:col-span-6"></div>
              </div>
            )}
            <div className=" hidden lg:block">
              {bfsData && (
                <CollectionFilter
                  filter={bfsData?.filter}
                  state={state}
                  dispatch={dispatch}
                  refetch={refetch}
                  relatedCollections={relatedCollections}
                  collectionId={collectionId}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-6">
        <div
          ref={sortingRef}
          className="fixed left-1/2 z-10 md:mt-4 flex w-screen -translate-x-1/2 items-center justify-between bg-white px-5 mt-0 pt-1 pb-1 md:pt-2 md:pb-4 lg:relative lg:left-0 lg:mt-0 lg:mb-14 lg:h-14 lg:w-full lg:translate-x-0 lg:bg-hk-orange lg:px-4"
        >
          <div className="text-sm">
            Product Listing: {bfsData?.total_product}
          </div>
          <div className="hidden lg:block">
            <Sorting state={state} dispatch={dispatch} refetch={refetch} />
          </div>
        </div>

        <div className="fixed left-1/2 z-10 mt-10 w-screen -translate-x-1/2 px-5 lg:hidden">
          <MobileFilterSort
            state={state}
            dispatch={dispatch}
            filter={bfsData?.filter}
            refetch={refetch}
          />
        </div>

        <div className="grid grid-cols-2 gap-x-10 gap-y-6 pt-36 md:grid-cols-3 lg:grid-cols-4 lg:gap-y-10 lg:pt-0">
          <>
            {bfsData && bfsData.total_product > 0 ? (
              infiniteData &&
              infiniteData.pages.map((page) => {
                return page?.data?.map((p: ProductWithNodes, index: number) => {
                  if (p) {
                    return (
                      <Product
                        key={`${p.id}-${index}`}
                        storefrontProduct={p}
                        translations={{
                          sold_out_label:
                            translations?.product?.product_settings_translations
                              ?.sold_out_label,
                          add_to_cart_label:
                            translations?.product?.product_settings_translations
                              ?.add_to_cart_label,
                        }}
                      />
                    );
                  }
                  return null;
                });
              })
            ) : (
              <>{!bfsIsLoading && 'No products found'}</>
            )}
          </>
        </div>
        <div
          className={clsx([
            'mt-20 py-10',
            bfsIsLoading || isFetching || isFetchingNextPage
              ? 'opacity-100'
              : 'opacity-0',
          ])}
          ref={ref}
        >
          <div className="flex items-center justify-center">
            <LoadingSpinner className=" text-hk-orange" width="w-14" />
          </div>
        </div>
      </div>
    </div>
  );
}
